/**
 * Resolve paths to files in `public/` for Vite, honoring `base` in vite.config,
 * and append `?v=` so browsers fetch updated logos/images instead of stale cache.
 *
 * Version source:
 * - dev: session id (one per page load; env version is ignored so public/ edits bust cache)
 * - production: `VITE_ASSET_VERSION` from env or vite `define` (set on each build)
 */
let devSessionVersion;

function getAssetVersion() {
  // Dev: new ?v= each page load so replaced files in public/ are not served from cache
  // (VITE_ASSET_VERSION is fixed for the whole dev-server run).
  if (import.meta.env.DEV) {
    if (!devSessionVersion) devSessionVersion = Date.now().toString(36);
    return devSessionVersion;
  }
  const fromEnv = import.meta.env.VITE_ASSET_VERSION;
  if (fromEnv) return String(fromEnv);
  return '1';
}

function resolveBasePath(path) {
  if (path == null || typeof path !== 'string') return path;
  const base = import.meta.env.BASE_URL ?? '/';
  const root = base.endsWith('/') ? base.slice(0, -1) : base;
  if (path.startsWith('/')) {
    return root ? `${root}${path}` : path;
  }
  return root ? `${root}/${path}` : `/${path}`;
}

function appendCacheBust(resolved) {
  if (resolved == null || typeof resolved !== 'string') return resolved;
  if (resolved.startsWith('data:') || resolved.startsWith('blob:')) return resolved;
  const version = getAssetVersion();
  if (!version) return resolved;
  const q = resolved.indexOf('?');
  const pathPart = q === -1 ? resolved : resolved.slice(0, q);
  const params = new URLSearchParams(q === -1 ? '' : resolved.slice(q + 1));
  params.set('v', version);
  const query = params.toString();
  return query ? `${pathPart}?${query}` : pathPart;
}

/** @param {string} path */
export function assetUrl(path) {
  return appendCacheBust(resolveBasePath(path));
}

/** Use with `fetch(assetUrl(...), assetFetchInit)` so MP3/JSON loads bypass stale HTTP cache. */
export const assetFetchInit = { cache: 'no-store' };

/**
 * Route Pixi `Assets.load` URLs through {@link assetUrl}.
 * Call once before any textures are loaded.
 * @param {import('pixi.js').Assets} assets
 */
export function patchPixiAssetLoader(assets) {
  const nativeLoad = assets.load.bind(assets);
  assets.load = (input, ...args) => {
    if (typeof input === 'string') return nativeLoad(assetUrl(input), ...args);
    if (Array.isArray(input)) {
      return nativeLoad(
        input.map((item) => (typeof item === 'string' ? assetUrl(item) : item)),
        ...args,
      );
    }
    if (input && typeof input === 'object') {
      const mapped = {};
      for (const [key, val] of Object.entries(input)) {
        mapped[key] = typeof val === 'string' ? assetUrl(val) : val;
      }
      return nativeLoad(mapped, ...args);
    }
    return nativeLoad(input, ...args);
  };
}
